import type { VideoModel } from "@ziggapp/klap-core/video";
import type { PublicationModel } from "../../types/models";
import { isEditablePersistedPub } from "./engine/status";
import { pickScheduleEntryVideoId } from "./scheduledProjects";

// §16 select step. The step picks what is NEW to schedule. A short holding a
// committed post is the project's plan already: it rides into the dialog as
// a context row (§16.1) whatever its tile says, so the tile renders selected
// and locked, never counts, and never becomes an entry video — an entry
// video gets §6.4 drafts for every provider its posts do not cover, which is
// not what ticking a scheduled short means. With nothing new selected and a
// plan in the folder, Continue opens that plan on the read face instead.

export type SelectStepContinue = "schedule" | "open" | "none";

export type SelectStepState = {
  /** The shorts the picker can toggle: the ones without a committed post. */
  selectableVideos: VideoModel[];
  /** The entry set — the selected shorts among the selectable ones, in
   *  folder order. */
  selectedNewVideos: VideoModel[];
  areAllSelectableSelected: boolean;
  /** What the footer button does: schedule the new shorts, open the
   *  folder's plan (nothing new selected, a committed short exists), or
   *  nothing (nothing new selected, no plan). */
  continueKind: SelectStepContinue;
};

export const getSelectStepState = ({
  videos,
  committedVideoIds,
  selectedVideoIds,
}: {
  videos: readonly VideoModel[];
  committedVideoIds: ReadonlySet<string>;
  selectedVideoIds: readonly string[];
}): SelectStepState => {
  const selectedIdSet = new Set(selectedVideoIds);
  const selectableVideos = videos.filter(
    (video) => !committedVideoIds.has(video.id),
  );
  const selectedNewVideos = selectableVideos.filter((video) =>
    selectedIdSet.has(video.id),
  );
  const hasPlan = videos.some((video) => committedVideoIds.has(video.id));
  return {
    selectableVideos,
    selectedNewVideos,
    areAllSelectableSelected:
      selectableVideos.length > 0 &&
      selectedNewVideos.length === selectableVideos.length,
    continueKind:
      selectedNewVideos.length > 0 ? "schedule" : hasPlan ? "open" : "none",
  };
};

export const getSelectStepContinueLabel = (
  kind: SelectStepContinue,
): string => (kind === "open" ? "Open schedule" : "Continue to scheduling");

/**
 * The short the plan opens on: the one the user came from when it is
 * committed (a committed short's own publish button), else the short
 * holding the folder's next post (`pickScheduleEntryVideoId`, the project
 * chip's rule), else the first committed short in folder order. Null when
 * the folder holds no plan.
 */
export const pickSelectStepEntryVideoId = ({
  publications,
  videos,
  committedVideoIds,
  preferredVideoIds = [],
}: {
  publications: readonly PublicationModel[];
  videos: readonly VideoModel[];
  committedVideoIds: ReadonlySet<string>;
  preferredVideoIds?: readonly string[];
}): string | null => {
  const committedInFolder = videos
    .filter((video) => committedVideoIds.has(video.id))
    .map((video) => video.id);
  if (committedInFolder.length === 0) return null;
  const committedSet = new Set(committedInFolder);
  const preferred = preferredVideoIds.find((id) => committedSet.has(id));
  if (preferred) return preferred;
  return (
    pickScheduleEntryVideoId({ publications, videoIds: committedInFolder }) ??
    committedInFolder[0]
  );
};

/**
 * Edit mode's header picker (`mode=edit`) includes every session short,
 * including published context and opening drafts. Keep entry shorts in the
 * pool after deselection so they can be restored. Outside that scope, only
 * shorts with editable persisted posts (scheduled / failed) may be added.
 * The picker separately locks selected shorts with immutable history.
 */
export const selectEditModePickerVideos = (
  videos: readonly VideoModel[],
  publications: readonly Pick<PublicationModel, "projectId" | "status">[],
  sessionVideoIds: readonly string[] = [],
): VideoModel[] => {
  const offeredVideoIds = new Set([
    ...sessionVideoIds,
    ...publications
      .filter((pub) => pub.projectId && isEditablePersistedPub(pub))
      .map((pub) => pub.projectId as string),
  ]);
  return videos.filter((video) => offeredVideoIds.has(video.id));
};
